import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { JourneyTabType } from './types/tabs';
import { TabNavigation, type TabConfig } from '../../components/TabNavigation';
import { JourneyTemplates } from './components/JourneyTemplates';
import { JourneyUserProgress } from './components/JourneyUserProgress';
import { JourneyLeaderboard } from './components/JourneyLeaderboard';
import { useAuth } from '../../hooks/useAuth';
import { journeyService } from './services/journeyService';

const tabs: TabConfig<JourneyTabType>[] = [
    { id: 'templates', label: 'Journey Templates', icon: '🗺️' },
    { id: 'progress', label: 'User Progress', icon: '👤' },
    { id: 'leaderboard', label: 'Leaderboard', icon: '🏆' },
];

const isValidTab = (tab: string | null): tab is JourneyTabType => {
    return tab === 'templates' || tab === 'progress' || tab === 'leaderboard';
};

export const Journey = () => {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<JourneyTabType>('templates');
    const [isResetting, setIsResetting] = useState(false);

    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (isValidTab(tabParam)) {
            setActiveTab(tabParam);
        } else {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('tab', 'templates');
                    return next;
                },
                { replace: true },
            );
        }
    }, [searchParams, setSearchParams]);

    const handleTabChange = (tab: JourneyTabType) => {
        setActiveTab(tab);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('tab', tab);
                return next;
            },
            { replace: true },
        );
    };

    const handleResetJourney = async () => {
        if (!user) return;

        const confirmed = window.confirm(
            'Are you sure you want to reset your journey? This will delete all compiled chapters, sections, items, and journey state. This cannot be undone.',
        );
        if (!confirmed) return;

        setIsResetting(true);
        try {
            await journeyService.resetUserJourney(user.uid);
            alert('Journey reset successfully.');
        } catch (error) {
            console.error('Failed to reset journey:', error);
            alert('Failed to reset journey. Check the console for details.');
        } finally {
            setIsResetting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-4 md:py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-6 md:mb-8 flex flex-wrap items-center justify-between gap-2">
                    <h1 className="text-3xl font-bold text-gray-900">Journey</h1>
                    <button
                        onClick={handleResetJourney}
                        disabled={isResetting}
                        className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isResetting ? 'Resetting...' : 'Reset My Journey'}
                    </button>
                </div>

                <TabNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />

                {activeTab === 'templates' && <JourneyTemplates />}
                {activeTab === 'progress' && <JourneyUserProgress />}
                {activeTab === 'leaderboard' && <JourneyLeaderboard />}
            </div>
        </div>
    );
};
